import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, Leaf, ChevronDown } from "lucide-react";

const NAV = [
  { label: "About Ayurveda", to: "/about" },
  {
    label: "Ayurveda",
    to: "/articles",
    children: [
      { label: "Doshas & Basics", to: "/tags/ayurveda-basics" },
      { label: "Understanding the Doshas", to: "/articles/understanding-the-three-doshas" },
      { label: "Kerala Ayurveda", to: "/tags/kerala-ayurveda" },
      { label: "Seasonal Wellness", to: "/tags/seasonal-wellness" },
      { label: "Digestion & Immunity", to: "/tags/digestion" },
    ],
  },
  {
    label: "Therapies",
    to: "/therapies",
    children: [
      { label: "All Therapies", to: "/therapies" },
      { label: "Abhyanga — Oil Massage", to: "/therapies/abhyanga" },
      { label: "Shirodhara", to: "/therapies/shirodhara" },
      { label: "Panchakarma", to: "/therapies/panchakarma" },
      { label: "Nasya", to: "/therapies/nasya" },
    ],
  },
  {
    label: "Nature's Pharmacy",
    to: "/herbs",
    children: [
      { label: "Herb Library", to: "/herbs" },
      { label: "Ashwagandha", to: "/herbs/ashwagandha" },
      { label: "Turmeric", to: "/herbs/turmeric" },
      { label: "Triphala", to: "/herbs/triphala" },
      { label: "Tulsi (Holy Basil)", to: "/herbs/tulsi" },
    ],
  },
  { label: "Articles", to: "/articles" },
  { label: "AI Assistants", to: "/assistants" },
  { label: "Network", to: "/network" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-brand-line/60"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex h-[70px] items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand text-brand-sand">
              <Leaf className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <span className="font-heading font-bold text-2xl sm:text-3xl text-brand tracking-tight">
              Ayurvedam<span className="text-brand-clay">.com</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="group relative">
                  <button
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="flex items-center gap-1 rounded-full px-3.5 py-2 text-[15px] font-medium text-brand-muted transition-colors hover:text-brand hover:bg-brand-mist/60"
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="invisible absolute left-0 top-full z-50 min-w-[240px] translate-y-1 rounded-xl border border-brand-line bg-white p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-child-${c.to.replace(/\//g, "-")}`}
                        className="block rounded-lg px-3 py-2 text-sm text-brand-ink transition-colors hover:bg-brand/5 hover:text-brand"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-full px-3.5 py-2 text-[15px] font-medium transition-colors hover:text-brand hover:bg-brand-mist/60 ${
                      isActive ? "text-brand" : "text-brand-muted"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <form onSubmit={submit} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
              <input
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="h-10 w-44 rounded-full border border-brand-line bg-brand-mist/60 pl-9 pr-4 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </form>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-brand-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden max-h-[80vh] overflow-y-auto border-t border-brand-line bg-brand-sand px-5 py-4 space-y-2">
          <form onSubmit={submit} className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Ayurveda…"
              className="h-11 w-full rounded-full border border-brand-line bg-white pl-9 pr-4 text-sm outline-none"
            />
          </form>
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="border-b border-brand-line/50 pb-2">
                <button
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => setExpanded((e) => (e === item.label ? null : item.label))}
                  className="flex w-full items-center justify-between py-2 text-lg font-medium text-brand-ink"
                >
                  {item.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${expanded === item.label ? "rotate-180" : ""}`} />
                </button>
                {expanded === item.label && (
                  <div className="ml-3 space-y-1 pb-1">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="block py-1.5 text-sm text-brand-muted"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="block border-b border-brand-line/50 py-2 text-lg font-medium text-brand-ink"
              >
                {item.label}
              </Link>
            )
          )}
        </div>
      )}
    </header>
  );
}
